import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { BrandEnum } from '@infrastructure/brand.enum';
import { ModelModel } from '@models/api-models/model';
import { AssortmentModel } from '@models/assortment-model';
import { ComponentVariantModel } from '@models/component-variant';
import { ConfigurationModel } from '@models/configuration';
import { ModelGroupModel } from '@models/model-group';
import { ModelSizeModel } from '@models/model-size';
import { TranslatePipe } from '@ngx-translate/core';
import { DateToLocalPipe } from '@pipes/date.pipe';
import { BrandModeService } from '@services/brandmode.service';
import { LanguageService } from '@services/language.service';
import { ComponentCodeConstants } from '@shared/component-constants';
import { ModelgroupConstants } from '@shared/modelgroup-constants';
import { Subscription } from 'rxjs';
import { FirstTimeDeliveryComponent } from '../first-time-delivery/first-time-delivery.component';

@Component({
	selector: 'availability-text',
	templateUrl: "availability-text.component.html",
	styleUrls: ['./availability-text.component.scss'],
	imports: [CommonModule, TranslatePipe, FirstTimeDeliveryComponent, DateToLocalPipe],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: true
})
export class AvailabilityComponent implements OnChanges, OnDestroy, OnInit {

	@Input() model: ModelModel | undefined | null;

	// used for discontiuned
	@Input() date?: Date | null;
	@Input() modelgroup?: ModelGroupModel | null;
	@Input() modelSize!: ModelSizeModel;
	@Input() configuration!: ConfigurationModel;
	@Input() variants!: Array<ComponentVariantModel>;

	// used for new fields
	@Input() new?: boolean;
	@Input() omitDiscontinuedLabel!: boolean;
	@Input() useNewDetails!: boolean;
	@Input() newLargeText!: boolean;
	@Input() variantOrModelIsNew: boolean;
	@Input() showAllNewflags!: boolean;

	public newText: string | null = null;
	public newDate: Date | null = null;

	public discontinuedModel: { discontinuedprefixText?: string, date: Date, omitDiscontinuedLabel: boolean } | null = null;
	private langSubscription: Subscription;
	constructor(private languageService: LanguageService, private brand: BrandModeService) {

	}

	ngOnInit(): void {
		this.langSubscription = this.languageService.$onLangChange?.subscribe(() => {
			this.ngOnChanges();
		});

	}

	get AllAssortments(): Array<AssortmentModel | undefined> {
		const assortments = new Array<AssortmentModel | undefined>();
		this.model?.AllAssortments.forEach(x => assortments.push(x));
		this.modelSize?.Assortments.forEach(x => assortments.push(x));
		this.variants?.forEach(x => x.AllAssortments.forEach(t => assortments.push(t)));

		return assortments.distinct((x, y) => x?.Code === y?.Code);
	}

	ngOnDestroy() {
		if (!this.langSubscription?.closed) {
			this.langSubscription?.unsubscribe();
		}
	}

	ngOnChanges() {
		this.newDate = null;
		this.newText = null;
		this.discontinuedModel = null;

		// set discontinued prefix
		if (!this.date && this.model && this.modelSize && this.configuration && this.variants) {
			if (this.modelgroup?.Discontinued) {
				this.discontinuedModel = { discontinuedprefixText: "", date: this.modelgroup.Discontinued, omitDiscontinuedLabel: false };
			}

			if (this.model.Discontinued && this.discontinuedModel == null || (this.model.Discontinued && this.discontinuedModel && this.model.Discontinued < this.discontinuedModel.date)) {
				this.discontinuedModel = { discontinuedprefixText: this.languageService.instant(`CUSTOMIZER.COMPONENTS.MODEL.NAME`), date: this.model.Discontinued, omitDiscontinuedLabel: false };
			}

			if (this.modelSize.Discontinued && this.discontinuedModel == null || (this.modelSize.Discontinued && this.discontinuedModel && this.modelSize.Discontinued < this.discontinuedModel.date)) {
				this.discontinuedModel = { discontinuedprefixText: this.languageService.instant(`COMMON.SIZE`), date: this.modelSize.Discontinued, omitDiscontinuedLabel: false };
			}

			if (this.configuration.Discontinued && this.discontinuedModel == null || (this.configuration.Discontinued && this.discontinuedModel && this.configuration.Discontinued < this.discontinuedModel.date)) {
				this.discontinuedModel = { discontinuedprefixText: this.languageService.instant(`COMMON.COLOR`), date: this.configuration.Discontinued, omitDiscontinuedLabel: false };
			}

			this.variants.forEach(variant => {
				if (variant.Discontinued && this.discontinuedModel == null || (variant.Discontinued && this.discontinuedModel && variant.Discontinued < this.discontinuedModel.date)) {
					this.discontinuedModel = { discontinuedprefixText: variant.Component.Description, date: variant.Discontinued, omitDiscontinuedLabel: variant.OmitDiscontinuedLabel };
				}

				if (variant.VariantColor?.Discontinued && this.discontinuedModel == null || (variant.VariantColor?.Discontinued && this.discontinuedModel && variant.VariantColor.Discontinued < this.discontinuedModel?.date)) {
					this.discontinuedModel = { discontinuedprefixText: this.languageService.instant(`COMMON.COLOR`), date: variant.VariantColor.Discontinued, omitDiscontinuedLabel: variant.OmitDiscontinuedLabel };
				}

				if (variant.VariantSize?.Discontinued && this.discontinuedModel == null || (variant.VariantSize?.Discontinued && this.discontinuedModel && variant.VariantSize.Discontinued < this.discontinuedModel?.date)) {
					this.discontinuedModel = { discontinuedprefixText: this.languageService.instant(`COMMON.SIZE`), date: variant.VariantSize.Discontinued, omitDiscontinuedLabel: variant.OmitDiscontinuedLabel };
				}
			});

			this.newDate = this.discontinuedModel?.date ? new Date(this.discontinuedModel.date) : null;
		}
		else if (!this.date && this.modelgroup) {
			if (this.modelgroup.Discontinued) {
				let discontinuedprefixText = this.modelgroup.Description?.Fallback;

				if (this.modelgroup.ParentModelGroupDescription?.Fallback) {
					discontinuedprefixText = this.modelgroup.ParentModelGroupDescription.Fallback;
				}

				this.discontinuedModel = { discontinuedprefixText: discontinuedprefixText, date: this.modelgroup.Discontinued, omitDiscontinuedLabel: false };
			}

			this.newDate = this.discontinuedModel?.date ? new Date(this.discontinuedModel.date) : null;
		}
		else {
			this.newDate = this.date ? new Date(this.date) : null;
		}

		// set new text
		if (!this.variantOrModelIsNew) {
			if (this.new) {
				this.newText = this.languageService.instant(`COMMON.NEW`);
			}
			else if (this.useNewDetails && this.model) {
				this.setSizeNewtext();
				this.SetColorNewtext();
				this.setComponentNewtext();
			}
		}
	}

	private setComponentNewtext() {
		if (this.brand.currentBrandValue() === BrandEnum.Titanium && this.model?.NewComponentCodes) {
			this.model.NewComponentCodes
				.filter(componentCode => componentCode == ComponentCodeConstants.Temple || componentCode == ComponentCodeConstants.Innerrim)
				.forEach(componentCode => {
					if (!this.newText) {
						this.newText = componentCode == ComponentCodeConstants.Temple
							? this.languageService.instant(`COMMON.NEWTEMPLE`)
							: this.languageService.instant(`COMMON.NEWINNERRIM`);

					}
					else {
						this.newText += " + " + (componentCode == ComponentCodeConstants.Temple
							? this.languageService.instant(`COMMON.TEMPLE`)
							: this.languageService.instant(`COMMON.INNERRIM`));
					}
				});
		}
		else if (this.brand.currentBrandValue() === BrandEnum.Precious && this.model?.NewComponentCodesPrecious) {
			this.model.NewComponentCodesPrecious
				.filter(componentCode => componentCode == ComponentCodeConstants.Temple || componentCode == ComponentCodeConstants.Innerrim)
				.forEach(componentCode => {
					if (!this.newText) {

						this.newText = componentCode == ComponentCodeConstants.Temple
							? this.languageService.instant(`COMMON.NEWTEMPLE`)
							: this.languageService.instant(`COMMON.NEWINNERRIM`);

					}
					else {
						this.newText += " + " + (componentCode == ComponentCodeConstants.Temple
							? this.languageService.instant(`COMMON.TEMPLE`)
							: this.languageService.instant(`COMMON.INNERRIM`));
					}
				});
		}
	}

	private SetColorNewtext() {
		if (this.brand.currentBrandValue() === BrandEnum.Titanium && this.model?.NewColorComponentCodes) {
			let newRelevantColors: Array<string> = [];

			if (this.showAllNewflags) {
				newRelevantColors = this.model.NewColorComponentCodes;
			}
			else {
				newRelevantColors = this.model.NewColorComponentCodes.filter(componentCode => componentCode == ComponentCodeConstants.Innerrim);

				if (this.model.ModelGroupCode == ModelgroupConstants.STRIP9800) {
					newRelevantColors = newRelevantColors.concat(this.model.NewColorComponentCodes.filter(componentCode => componentCode == ComponentCodeConstants.Upperrim));
				}

				newRelevantColors = newRelevantColors.concat(this.model.NewColorComponentCodes.filter(x => x == "NewConfig"));
			}


			if (newRelevantColors.length > 0) {
				if (!this.newText) {
					this.newText = this.languageService.instant(`COMMON.NEWCOLOR`);
				}
				else {
					this.newText += " + " + this.languageService.instant(`COMMON.COLOR`);
				}
			}
		}
	}

	private setSizeNewtext() {
		if (this.brand.currentBrandValue() === BrandEnum.Titanium && this.model?.NewSizeComponentCodes) {
			this.model.NewSizeComponentCodes.forEach(newSizeComponentCode => {
				if (!this.model?.NewComponentCodes?.some(x => x == newSizeComponentCode)) {
					this.newText = this.languageService.instant(`COMMON.NEWSIZE`);
				}
			});
		}
		else if (this.brand.currentBrandValue() === BrandEnum.Precious && this.model?.NewSizeComponentCodesPrecious) {
			this.model.NewSizeComponentCodesPrecious.forEach(newSizeComponentCode => {
				if (!this.model?.NewComponentCodes?.some(x => x == newSizeComponentCode)) {
					this.newText = this.languageService.instant(`COMMON.NEWSIZE`);
				}
			});
		}
	}

}
